import React from "react";
import { motion } from "framer-motion";
import { CheckCircle2, FlaskConical, BarChart3, Globe2, Stethoscope, Sigma, Landmark, Briefcase, Zap } from "lucide-react";
import { fade, Eyebrow, PageHeader, usePageMeta } from "@/components/SiteLayout";

const MATURITY_SEGMENTS = [
  { title: "Early evidence planning", desc: "Shape the evidence strategy while every option is still open — comparators, endpoints, HRQoL, RWE." },
  { title: "Preparing for Phase III", desc: "Stress test the protocol against future JCA PICOs before it is locked. This window never reopens." },
  { title: "Preparing the Article 9 dossier", desc: "Pressure-test the JCA submission dossier the way assessors will — before submission, not after the questions arrive." },
  { title: "Preparing National HTA", desc: "Convert the JCA report into country-level value defence, pricing strategy and adaptation plans." },
  { title: "Post-JCA optimization", desc: "Govern the evidence lifecycle: reassessments, new indications, and lessons from every published JCA." },
];

const ENGAGEMENT_SCENARIOS = [
  "Single-arm oncology assets",
  "Rare diseases",
  "Cell & Gene Therapy",
  "External comparator strategy",
  "Multi-country HTA",
  "Precision medicine",
];

const STAGE_FIT = [
  { stage: "Phase I", fit: 3 },
  { stage: "Phase II", fit: 5 },
  { stage: "Phase III", fit: 5 },
  { stage: "Pre-submission", fit: 5 },
  { stage: "During JCA", fit: 4 },
  { stage: "Post-JCA", fit: 3 },
];

const BUYING_QUESTIONS = [
  "Can we defend this asset if JCA started tomorrow?",
  "What evidence should we still generate — while we still can?",
  "Would an assessor accept our indirect comparison?",
  "Which PICOs could sink the assessment?",
  "How will the JCA report be used against us in national negotiations?",
  "What should the board prioritize over the next 6–24 months?",
];

const STAKEHOLDERS = [
  { icon: FlaskConical, role: "Clinical Development", line: "Design trials that survive future PICOs" },
  { icon: BarChart3, role: "HEOR", line: "Own the evidence strategy and its defensibility" },
  { icon: Globe2, role: "Market Access", line: "Convert evidence into access and price" },
  { icon: Stethoscope, role: "Medical Affairs", line: "Carry the value narrative into every market" },
  { icon: Sigma, role: "Biostatistics", line: "Make the ITC/MAIC methodology assessor-proof" },
  { icon: Landmark, role: "Global Value & Access", line: "Align portfolio investment with evidence risk" },
  { icon: Briefcase, role: "Executive Leadership", line: "Decide with a verdict, not a dashboard" },
];

const ENGAGEMENT_TRIGGERS = [
  "Before governance review",
  "After a failed internal readiness review",
  "When external comparators become uncertain",
  "Before pivotal trial design",
  "Before EU launch planning",
  "Following publication of a competitor JCA",
];

export default function WhoWeServe() {
  usePageMeta(
    "Who We Serve — JCA Readiness for Pharma & Biotech | JCA Matrix™",
    "From early evidence planning to post-JCA optimization: HEOR, Market Access, Medical Affairs and leadership teams preparing assets for Europe's Joint Clinical Assessment."
  );
  return (
    <>
      <PageHeader
        eyebrow="Who we serve"
        title={<>Built for teams shaping tomorrow's JCA evidence <span className="text-blue-600">today</span>.</>}
      />

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-8" data-testid="who-we-serve">
            <div className="lg:col-span-7 space-y-6">
              <div className="rounded-2xl border-2 border-blue-600 bg-white p-8 shadow-[0_20px_60px_-24px_rgba(37,99,235,0.3)]">
                <div className="text-xs font-semibold uppercase tracking-widest text-blue-600">Where are you on the evidence journey?</div>
                <div className="mt-5 space-y-3" data-testid="maturity-segments">
                  {MATURITY_SEGMENTS.map((s, i) => (
                    <motion.div key={s.title} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                      className="rounded-xl border border-slate-200 bg-white px-5 py-4 hover:border-blue-300 hover:bg-blue-50/30 hover:-translate-y-0.5 transition-all duration-300"
                      data-testid={`maturity-segment-${i}`}>
                      <div className="font-display font-semibold text-slate-900">{s.title}</div>
                      <p className="mt-1 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                    </motion.div>
                  ))}
                </div>
                <div className="mt-5 flex flex-wrap gap-2">
                  {["Clinical-stage biotech", "Specialty pharma", "Mid-size pharma", "Global pharma"].map((t) => (
                    <span key={t} className="rounded-lg bg-blue-50 border border-blue-200/60 px-3 py-1.5 text-xs font-medium text-blue-700">{t}</span>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-7" data-testid="engagement-scenarios">
                <div className="text-xs font-semibold uppercase tracking-widest text-slate-400">Typical engagement scenarios</div>
                <div className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                  {ENGAGEMENT_SCENARIOS.map((t) => (
                    <span key={t} className="flex items-center gap-2 text-sm font-medium text-slate-700">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" /> {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="rounded-2xl border border-slate-200 bg-white p-7 h-full flex flex-col justify-center" data-testid="stage-fit">
                <div className="text-xs font-semibold uppercase tracking-widest text-slate-400">When we create the most value</div>
                <div className="mt-4 space-y-3">
                  {STAGE_FIT.map((s) => (
                    <div key={s.stage} className="flex items-center gap-3">
                      <span className="text-sm text-slate-600 w-32 shrink-0">{s.stage}</span>
                      <div className="flex gap-1 flex-1">
                        {[1, 2, 3, 4, 5].map((n) => <span key={n} className={`h-2 flex-1 rounded-sm ${n <= s.fit ? "bg-blue-600" : "bg-slate-100"}`} />)}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-xs text-slate-500">Phase II is the ideal moment — evidence gaps can still be addressed, not just documented.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What you're really buying */}
      <section className="py-24 bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <Eyebrow>What you're really buying</Eyebrow>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              Not AI. <span className="text-blue-600">Confidence.</span>
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              The AI enables the analysis — the deliverable is strategic guidance. Every engagement answers the
              questions your executive team is already asking:
            </p>
          </div>
          <div className="mt-12 max-w-3xl mx-auto rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100 shadow-sm" data-testid="buying-questions">
            {BUYING_QUESTIONS.map((q, i) => (
              <motion.div key={q} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                className="px-6 py-4 flex items-center gap-4">
                <span className="h-7 w-7 rounded-full bg-blue-50 border border-blue-200/60 text-blue-600 flex items-center justify-center text-sm font-semibold shrink-0">?</span>
                <span className="text-sm sm:text-base text-slate-700 font-medium italic">“{q}”</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stakeholders */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <Eyebrow>Who's in the room</Eyebrow>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              Typical stakeholders we work with
            </h2>
          </div>
          <div className="mt-12 flex flex-wrap lg:flex-nowrap justify-center gap-3" data-testid="stakeholders-grid">
            {STAKEHOLDERS.map((s, i) => (
              <motion.div key={s.role} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                className="flex-1 min-w-[150px] rounded-2xl border border-slate-200 bg-white px-4 py-5 text-center hover:-translate-y-1 hover:shadow-lg hover:shadow-slate-900/5 hover:border-blue-200 transition-all duration-300"
                data-testid={`stakeholder-${i}`}>
                <div className="mx-auto h-10 w-10 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center">
                  <s.icon className="h-5 w-5 text-blue-600" strokeWidth={1.75} />
                </div>
                <h3 className="mt-3 font-display text-sm font-semibold tracking-tight leading-snug">{s.role}</h3>
                <p className="mt-1.5 text-xs text-slate-500 leading-relaxed">{s.line}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Engagement triggers */}
      <section className="py-24 bg-slate-50 border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <Eyebrow>Real triggers</Eyebrow>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              Why clients usually engage us
            </h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="engagement-triggers">
            {ENGAGEMENT_TRIGGERS.map((t, i) => (
              <motion.div key={t} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 hover:-translate-y-0.5 hover:border-blue-300 hover:shadow-md transition-all duration-300"
                data-testid={`trigger-${i}`}>
                <Zap className="h-4 w-4 text-blue-600 shrink-0" />
                <span className="text-sm font-medium text-slate-800">{t}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
